import React, { useState, useEffect, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
import { NotificationContext } from '../../contexts/notificationContext'
import './issue-management.scss'
import {
  Box,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  TableSortLabel,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  Download as DownloadIcon
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import IssueForm from './components/IssueForm/IssueForm'
import IssueDetail from './components/IssueDetail/IssueDetail'
import IssueDashboard from './components/IssueDashboard/IssueDashboard'
import {
  fetchIssues,
  createIssue,
  updateIssue,
  deleteIssue,
  uploadAttachment,
} from './services/issueService'

const IssueManagement = () => {
  const [searchParams] = useSearchParams()
  const { addIssueNotification } = useContext(NotificationContext)

  // State management
  const [issues, setIssues] = useState([])
  const [filteredIssues, setFilteredIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedIssue, setSelectedIssue] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [issueToDelete, setIssueToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [plantFilter, setPlantFilter] = useState([])
  const [escalationFilter, setEscalationFilter] = useState('all')
  const [issueTypeFilter, setIssueTypeFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [assignedToFilter, setAssignedToFilter] = useState('all')
  const [dateRange, setDateRange] = useState({
    from: dayjs().subtract(30, 'day'),
    to: dayjs(),
  })
  const [tab, setTab] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [sortBy, setSortBy] = useState('plantName') 
  const [sortOrder, setSortOrder] = useState('asc')

  // Get unique plants from issues
  const plantOptions = React.useMemo(() => {
    const uniquePlants = [
      ...new Set(issues.map((issue) => issue.plantId).filter(Boolean)),
    ]
    return uniquePlants.map((plantId) => {
      const issue = issues.find((i) => i.plantId === plantId)
      return {
        id: plantId,
        name: issue?.plantName || plantId,
      }
    })
  }, [issues])

  // Initialize filters from URL parameters
  useEffect(() => {
    const plantId = searchParams.get('plantId')
    const plantName = searchParams.get('plantName')

    if (plantId) {  
      console.log('Displaying issues for', plantId, '-' ,plantName, 'from URL params.')
      setPlantFilter([plantId])
      //switch to table tab
      setTab(1)
      console.log('Switched to Table tab and set plant filter')
    }
  }, [searchParams])

  // Constants
  const statusOptions = [
    'Open',
    'Under Review',
    'Material Awaited',
    'OEM',
    'In Progress',
    'Resolved',
    'Closed',
    'Deferred',
  ]
  const priorityOptions = ['High', 'Medium', 'Low']
  const issueTypeOptions = [
    'Corrective',
    'Preventive',
    'Warranty',
    'Administrative',
  ]
  const categoryOptions = [
    'Electrical',
    'Communication / RMS',
    'Structural / Mounting',
    'Cleaning / Soiling',
    'Civil / Roof Related',
    'Earthing / Lightning',
    'Safety',
    'Regulatory / DISCOM',
    'Vendor / OEM',
    'Reporting / Documentation',
    'Others',
  ]
  const escalationOptions = [
    { label: 'L1 – Technician', value: 'Level 1' },
    { label: 'L2 – Supervisor', value: 'Level 2' },
    { label: 'L3 – Central Team', value: 'Level 3' },
    { label: 'L4 – Vendor', value: 'Level 4' },
    { label: 'L5 – Management', value: 'Level 5' },
  ]
  const assignedToOptions = [
    'Pallavi Band',
    'Ramsing Rajput',
    'Abhishek Deshmukh',
    'Ujjawala Thakare',
    'Vivekanada Yadav',
    'Suraj Yadav',
    'Dheerendra Yadav',
    'Sanket Jadhav',
    'Obeliwe Molepa',
    'Shahid Anwar',
  ]

  // Load issues on component mount
  useEffect(() => {
    loadIssues()
  }, [])

  useEffect(() => {
    if (showForm || showDetail || showDeleteModal) return

    // Set up real-time polling for updates (every 60 seconds)
    const intervalId = setInterval(() => {
      loadIssues()
    }, 60000)

    return () => clearInterval(intervalId)
  }, [showForm, showDetail, showDeleteModal])

  // Filter issues when filters change
  useEffect(() => {
    filterIssues()
  }, [
    issues,
    searchTerm,
    statusFilter,
    priorityFilter,
    escalationFilter,
    plantFilter,
    issueTypeFilter,
    categoryFilter,
    dateRange,
    assignedToFilter,
    sortBy,
    sortOrder,
  ])

  const loadIssues = async () => {
    try {
      setLoading(true)
      const data = await fetchIssues()
      const normalised = data.map((issue) => ({
        ...issue,
        id: issue.id?.$oid || issue.id || issue.id || null,
      }))
      setIssues(normalised)
      console.log('Issues loaded in issuemanagement.jsx:', normalised.length, 'issues')
      console.log('Plant IDs in issues:', [...new Set(normalised.map(i => i.plantId))])
    } catch (error) {
      console.error('Error loading issues:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterIssues = () => {
    let filtered = [...issues]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (issue) =>
          (issue.title || '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (issue.description || '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (issue.issueId || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((issue) => issue.status === statusFilter)
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter((issue) => issue.priority === priorityFilter)
    }

    // Plant filter
    if (plantFilter.length > 0) {
      filtered = filtered.filter((issue) => {
        const match = plantFilter.includes(issue.plantId)
        if (!match) {
          console.log('Issue plantId:', issue.plantId, 'not in filter:', plantFilter)
        }
        return match
      })
    }

    // Escalation filter
    if (escalationFilter !== 'all') {
      filtered = filtered.filter((issue) => {
        return (issue.escalationLevel || '') === escalationFilter
      })
    }

    //Issue Type filter
    if (issueTypeFilter !== 'all') {
      filtered = filtered.filter((issue) => issue.issueType === issueTypeFilter)
    }

    //Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((issue) => issue.category === categoryFilter)
    }

    //Assigned To Internal filter
    if (assignedToFilter !== 'all') {
      filtered = filtered.filter((issue) => issue.assignedTo === assignedToFilter)
    }

    // Date range filter
    filtered = filtered.filter((issue) => {
      const issueDate = dayjs(issue.createdAt)
      return (
        issueDate.isAfter(dateRange.from) &&
        issueDate.isBefore(dateRange.to.add(1, 'day'))
      )
    })

    if (sortBy) {
      filtered.sort((a, b) => {
        const aValue = a[sortBy] || ''
        const bValue = b[sortBy] || ''
        
        if (sortOrder === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      })
    }

    setFilteredIssues(filtered)
  }

  const handleTabChange = (event, newValue) => {
    setTab(newValue)
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      if (sortOrder === 'asc') {
        setSortOrder('desc')
      } else if (sortOrder === 'desc') {
        setSortBy(null)
        setSortOrder('asc')
      }
    } else {
      //If clicking a different column, start with ascending sort
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const handleCreateIssue = () => {
    setSelectedIssue(null)
    setEditMode(false)
    setShowForm(true)
  }

  const handleEditIssue = (issue) => {
    setSelectedIssue({
      ...issue,
      id: issue.id?.$oid || issue.id || issue.id || null,
    })
    setEditMode(true)
    setShowForm(true)
  }

  const handleViewIssue = (issue) => {
    //setSelectedIssue(issue)
    setSelectedIssue({
      ...issue,
      id: issue.id?.$oid || issue.id || issue.id || null,
    })
    setShowDetail(true)
  }

  const handleDeleteIssue = async (issue) => {
    const issueId = issue.id?.$oid || issue.id || issue.id
    console.log('Deleting issue with ID:', issueId)
    if (!issueId) {
      console.error('No valid ID found for issue:', issue)
      return
    }
    setIssueToDelete({ ...issue, id: issueId })
    setShowDeleteModal(true)
  }

  const confirmDeleteIssue = async () => {
    if (!issueToDelete) return

    try {
      await deleteIssue(issueToDelete.id)
      await loadIssues()
    } catch (error) {
      console.error('Error deleting issue', error)
    } finally {
      setShowDeleteModal(false)
      setIssueToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setIssueToDelete(null)
  }

  const handleFormSubmit = async (issueData, attachments = []) => {
    try {
      let createdOrUpdatedIssue
      console.log('issueData', issueData)
      console.log('attachments received:', attachments)
      
      if (editMode && selectedIssue) {
        await updateIssue(selectedIssue.id, issueData)
        createdOrUpdatedIssue = selectedIssue
        // Upload attachments for edited issue (new attachments only)
        const newAttachments = attachments.filter(att => !att.isUploaded && att.file)
        console.log('New attachments to upload in edit mode:', newAttachments)
        if (newAttachments.length > 0) {
          for (const att of newAttachments) {
            try {
              await uploadAttachment(selectedIssue.id, att.file)
              console.log('Attachment uploaded for Issue No.:', selectedIssue.id)
            } catch (error) {
              console.error('Error uploading attachment:', error)
            }
          }
        }
      } else {
        // First create the issue
        const response = await createIssue(issueData)
        createdOrUpdatedIssue = response
        console.log('Created issue with ID:', createdOrUpdatedIssue?.id || createdOrUpdatedIssue?.id)
        
        // Extract the issue ID - handle both id and id formats
        const issueId = createdOrUpdatedIssue?.id || createdOrUpdatedIssue?.id
        
        if (!issueId) {
          console.error('No issue ID returned from createIssue:', createdOrUpdatedIssue)
          throw new Error('Failed to get issue ID from created issue')
        }
        
        // Filter for attachments that need to be uploaded
        const attachmentsToUpload = attachments.filter(att => {
          const hasFile = !!att.file
          const notUploaded = !att.isUploaded
          console.log(`Attachment ${att.name}: hasFile=${hasFile}, notUploaded=${notUploaded}`)
          return hasFile && notUploaded
        })
        
        console.log('Attachments to upload:', attachmentsToUpload.length)
        
        if (attachmentsToUpload.length > 0) {
          console.log(`Starting upload of ${attachmentsToUpload.length} attachments for issue ${issueId}`)
          
          for (let i = 0; i < attachmentsToUpload.length; i++) {
            const att = attachmentsToUpload[i]
            try {
              console.log(`Uploading attachment ${i + 1}/${attachmentsToUpload.length}: ${att.name}`)
              const uploadedAttachment = await uploadAttachment(issueId, att.file)
              console.log('Attachment uploaded successfully:', uploadedAttachment)
            } catch (error) {
              console.error(`Error uploading attachment ${att.name}:`, error)
              // Continue with other attachments even if one fails
            }
          }
          
          console.log('All attachments processed')
        } else {
          console.log('No attachments to upload')
        }
        
        // Trigger notification for new high-priority issues
        if (
          createdOrUpdatedIssue &&
          (issueData.priority === 'High' || issueData.priority === 'Medium')
        ) {
          addIssueNotification({
            id: issueId,
            title: issueData.title,
            priority: issueData.priority,
            plantId: issueData.plantId,
            plantName: issueData.plantName || 'Unknown Plant',
          })
        }
      }
      setShowForm(false)
      setSelectedIssue(null)
      await loadIssues()
    } catch (error) {
      console.error('Error saving issue:', error)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setSelectedIssue(null)
    setEditMode(false)
  }

  const handleDetailClose = () => {
    setShowDetail(false)
    setSelectedIssue(null)
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'error'
      case 'Medium':
        return 'warning'
      case 'Low':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'error'
      case 'In Progress':
        return 'secondary'
      case 'OEM':
        return 'info'
      case 'Material Awaited':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getEscalationLevelStyle = (level) => {
    const levelMap = {
      'Level 1': {
        color: 'success',
        label: 'L1 – Site Engineer',
        icon: '🧰',
        designation: 'Technician',
      },
      'Level 2': {
        color: 'info',
        label: 'L2 – Regional Lead',
        icon: '⚙️',
        designation: 'Supervisor',
      },
      'Level 3': {
        color: 'warning',
        label: 'L3 – Asset Manager',
        icon: '📊',
        designation: 'Central Team',
      },
      'Level 4': {
        color: 'error',
        label: 'L4 – OEM / Vendor',
        icon: '🏭',
        designation: 'Vendor',
      },
      'Level 5': {
        color: 'secondary',
        label: 'L5 – Client / Mgmt',
        icon: '🚨',
        designation: 'Management',
      },
    }

    if (!level) {
      return {
        color: 'default',
        label: 'N/A',
        icon: null,
        designation: null,
      }
    }

    return (
      levelMap[level] || {
        color: 'default',
        label: level,
        icon: '❓',
        designation: 'Unknown',
      }
    )
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  const convertToCSV = (data, columns) => {
    const headers = columns.map(col => `"${col.header}"`).join(',')

    const rows = data.map(item => {
      return columns.map(col => {
        const value = col.accessor(item)

        if (value === null || value === undefined) return '""'
        const stringValue = String(value).replace(/"/g, '""')

        return `"${stringValue}"`
      }).join(',')
    })
    
    return [headers, ...rows].join('\n')
  }

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)  
  }

  const exportToCSV = () => {
    if (filteredIssues.length === 0) {
      console.warn("No issues found.")
      return
    }

    setIsExporting(true)
    try {
      const exportColumns = [
        { header: 'Site Name', accessor: (issue) => issue.plantName || 'N/A' },
        { header: 'Plant Capacity (kWp)', accessor: (issue) => issue.plantCapacity || 'N/A' },
        { header: 'Issue', accessor: (issue) => issue.title || 'N/A'},
        { header: 'Category', accessor: (issue) => issue.category || 'N/A' },
        { header: 'Priority', accessor: (issue) => issue.priority || 'N/A' },
        { header: 'Issue Type', accessor: (issue) => issue.issueType || 'N/A' },
        { header: 'Date Reported', accessor: (issue) => 
          issue.createdAt ? dayjs(issue.createdAt).format('DD-MM-YYYY') : 'N/A' 
        },
        { header: 'Assigned To (Internal)', accessor: (issue) => issue.assignedTo || 'N/A' },
        { header: 'Expected Closure Date', accessor: (issue) => 
          issue.expectedClosingDate ? dayjs(issue.expectedClosingDate).format('DD-MM-YYYY') : 'N/A' 
        },
        { header: 'Actual Closure Date', accessor: (issue) => 
          issue.actualClosingDate ? dayjs(issue.actualClosingDate).format('DD-MM-YYYY') : 'N/A' 
        },
        { header: 'Status', accessor: (issue) => issue.status || 'N/A' },
        { header: 'Delay', accessor: (issue) => issue.delayDays || 'N/A' },
        { header: 'Impact', accessor: (issue) => issue.severityImpact || 'N/A' },
        { header: 'Escalation Level', accessor: (issue) => issue.escalationLevel || 'N/A' },
        { header: 'Customer Communication', accessor: (issue) => 
          typeof issue.customerCommunicationDone === 'boolean' 
            ? (issue.customerCommunicationDone ? 'Y' : 'N')
            : 'N/A'
        },
        { header: 'Description', accessor: (issue) => issue.description || 'N/A' },
        { header: 'Root Cause', accessor: (issue) => issue.rootCausePreliminary || 'N/A'},
        { header: 'Action Plan', accessor: (issue) => issue.nextActionFollowUpPlan || 'N/A '}
      ]

      const csvData = convertToCSV(filteredIssues, exportColumns)
      const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss')
      const plantFilterText = plantFilter.length > 0 
        ? `${plantFilter.length}plants` 
        : ''
      const filename = `issues_${filteredIssues.length}_${plantFilterText}_${timestamp}.csv`

      downloadCSV(csvData, filename)
      console.log(`Exported ${filteredIssues.length} issues to CSV.`)

    } catch (error) {
      console.error('Error exporting to CSV:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="issue-management">
      <div className="issue-management-header">
        <Typography variant="h4" component="h1" className="page-title">
          Issue Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {tab === 1 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={isExporting ? <CircularProgress size ={16} /> : <DownloadIcon /> }
              onClick={exportToCSV}
              disabled={filteredIssues.length === 0 || isExporting}
              className="export-issues-btn"
            >
              Download ({filteredIssues.length})
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateIssue}
            className="create-issue-btn"
          >
            Create Issue
          </Button>
        </Box>
      </div>

      {/* Tabs: Dashboard / Table */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="Issue Management tabs"
        >
          <Tab label="Dashboard" />
          <Tab label="Table" />
        </Tabs>
      </Box>

      {/* Content depending on selected tab */}
      {tab === 0 && (
        // Dashboard tab: only show dashboard cards
        <IssueDashboard issues={filteredIssues} />
      )}

      {tab === 1 && (
        <>
          {/* Filters Section */}
          <Card className="filters-card" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    minWidth: '1200px',
                    pb: 1,
                    pt: 1,
                  }}
                >
                  {/* Plant Filter */}
                  <Autocomplete
                    multiple
                    size="small"
                    options={plantOptions}
                    getOptionLabel={(option) => option.name}
                    value={plantOptions.filter((plant) =>
                      plantFilter.includes(plant.id)
                    )}
                    onChange={(event, newValue) => {
                      setPlantFilter(newValue.map((plant) => plant.id))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Plant"
                        placeholder="Type to search..."
                        sx={{ minWidth: 200 }}
                      />
                    )}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase()
                      return options.filter(
                        (option) =>
                          option.name.toLowerCase().includes(inputValue) ||
                          option.id.toLowerCase().includes(inputValue)
                      )
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    sx={{ minWidth: 200 }}
                  />

                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Priority Filter */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      label="Priority"
                    >
                      <MenuItem value="all">All Priority</MenuItem>
                      {priorityOptions.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          {priority}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Issue Type Filter */}
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Issue Type</InputLabel>
                    <Select
                      value={issueTypeFilter}
                      onChange={(e) => setIssueTypeFilter(e.target.value)}
                      label="Issue Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {issueTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Category Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {categoryOptions.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Escalation Filter */}
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Escalation</InputLabel>
                    <Select
                      value={escalationFilter}
                      onChange={(e) => setEscalationFilter(e.target.value)}
                      label="Escalation"
                    >
                      <MenuItem value="all">All Levels</MenuItem>
                      {escalationOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Assigned To (Internal) Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Assigned To (Int.)</InputLabel>
                    <Select
                      value={assignedToFilter}
                      onChange={(e) => setAssignedToFilter(e.target.value)}
                      label="Assigned To (Int.)"
                    >
                      <MenuItem value="all">All Assignees</MenuItem>
                      {assignedToOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Date Range Filters */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box display="flex" gap={1} sx={{ minWidth: 300 }}>
                      <DatePicker
                        label="From"
                        format="DD-MM-YYYY"
                        value={dateRange.from}
                        onChange={(date) =>
                          setDateRange((prev) => ({ ...prev, from: date }))
                        }
                        slotProps={{
                          textField: { size: 'small', sx: { minWidth: 140 } },
                        }}
                      />
                      <DatePicker
                        label="To"
                        format="DD-MM-YYYY"
                        value={dateRange.to}
                        onChange={(date) =>
                          setDateRange((prev) => ({ ...prev, to: date }))
                        }
                        slotProps={{
                          textField: { size: 'small', sx: { minWidth: 140 } },
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Issues Table */}
          <Card className="issues-table-card" sx={{ mt: 2 }}>
            <CardContent>
              {/* Make table horizontally scrollable if too wide */}
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr. No.</TableCell>
                        {/* <TableCell>Issue ID</TableCell> */}
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'plantName'}
                            direction={sortBy === 'plantName' ? sortOrder : 'asc'}
                            onClick={() => handleSort('plantName')}
                          >
                            Site Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Plant Capacity (kWp)</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Issue Type</TableCell>
                        <TableCell>Date Reported</TableCell>
                        <TableCell>Reported By</TableCell>
                        <TableCell>Assigned To (Internal)</TableCell>
                        <TableCell>Assigned To (External)</TableCell>
                        <TableCell>Expected Closure Date</TableCell>
                        <TableCell>Actual Closure Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Overdue (Days)</TableCell>
                        <TableCell>Impact (Generation Loss / Risk)</TableCell>
                        <TableCell>Escalation Level</TableCell>
                        <TableCell>Customer Communication Done (Y/N)</TableCell>
                        <TableCell>Proof of Closure</TableCell>
                        <TableCell>Action Plan</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredIssues.map((issue, idx) => (
                        <TableRow key={issue.id} hover>
                          <TableCell>{idx + 1}</TableCell>
                          {/* <TableCell>{issue.issueId || 'N/A'}</TableCell> */}
                          <TableCell>{issue.plantName || 'N/A'}</TableCell>
                          <TableCell>{issue.plantCapacity ?? 'N/A'}</TableCell>
                          <TableCell>{issue.category || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={issue.priority || 'N/A'}
                              color={getPriorityColor(issue.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{issue.issueType || 'N/A'}</TableCell>
                          <TableCell>
                            {issue.createdAt
                              ? dayjs(issue.createdAt).format('DD-MM-YYYY')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{issue.reportedBy || 'N/A'}</TableCell>
                          <TableCell>{issue.assignedTo || 'N/A'}</TableCell>
                          <TableCell>
                            {issue.responsibleTeamExternal || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {issue.expectedClosingDate
                              ? dayjs(issue.expectedClosingDate).format(
                                  'DD-MM-YYYY'
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {issue.actualClosingDate
                              ? dayjs(issue.actualClosingDate).format(
                                  'DD-MM-YYYY'
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={issue.status || 'N/A'}
                              color={getStatusColor(issue.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{issue.delayDays ?? 'N/A'}</TableCell>
                          <TableCell>{issue.severityImpact || 'N/A'}</TableCell>
                          <TableCell>
                            {(() => {
                              const { color, label, icon, designation } =
                                getEscalationLevelStyle(issue.escalationLevel)

                              const tooltipText = designation
                                ? `This issue has been escalated to ${designation}`
                                : 'Escalation details not available'

                              return (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Chip
                                    label={`${icon ? icon + ' ' : ''}${
                                      issue.escalationLevel || label
                                    }`}
                                    color={color}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                  <Tooltip title={tooltipText} arrow>
                                    <IconButton
                                      size="small"
                                      sx={{ color: '#1976d2', cursor: 'help' }}
                                    >
                                      <InfoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )
                            })()}
                          </TableCell>
                          <TableCell>
                            {typeof issue.customerCommunicationDone ===
                            'boolean'
                              ? issue.customerCommunicationDone
                                ? 'Y'
                                : 'N'
                              : issue.customerCommunicationDone || 'N/A'}
                          </TableCell>
                          <TableCell>{issue.proofOfClosure || 'N/A'}</TableCell>
                          <TableCell>
                            {issue.nextActionFollowUpPlan || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleViewIssue(issue)}
                              title="View Details"
                            >
                              <ViewIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditIssue(issue)}
                              title="Edit Issue"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteIssue(issue)}
                              title="Delete Issue"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredIssues.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={24} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No issues found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Issue Form Dialog */}
      <Dialog
        open={showForm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleFormClose()
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Issue' : 'Create New Issue'}
          <IconButton
            onClick={handleFormClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <IssueForm
            issue={selectedIssue}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
            categoryOptions={categoryOptions}
            statusOptions={statusOptions}
            priorityOptions={priorityOptions}
            issueTypeOptions={issueTypeOptions}
            escalationOptions={escalationOptions}
            assignedToOptions={assignedToOptions}
          />
        </DialogContent>
      </Dialog>

      {/* Issue Detail Dialog */}
      <Dialog
        open={showDetail}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleDetailClose();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Issue Details
          <IconButton
            onClick={handleDetailClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <IssueDetail
              issue={selectedIssue}
              onEdit={() => {
                setShowDetail(false)
                handleEditIssue(selectedIssue)
              }}
              onClose={handleDetailClose}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Delete Issue modal */}
      <Dialog
        open={showDeleteModal}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Issue
          <IconButton
            onClick={handleCancelDelete}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You are about to delete issue{' '}
            <strong>"{issueToDelete?.title}"</strong>. Do you want to proceed?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteIssue}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default IssueManagement